// ── REAL-TIME CONFIGURATOR (VR tour add-on) ──────────────────────────────
//
// Brought back from the legacy Combined calculator's "Real-Time Customizer"
// (Bernard, 2026-10-26): inside a VR tour the buyer switches between finish,
// material and furniture options in real time. Pricing (Bernard, 2026-10-26):
//   - a flat $1,000 activation fee, once per VR section, plus
//   - per standard category (flooring, cabinets, …): the 1st option is
//     covered by the activation, the 2nd is +$700, each one after is +$500.
//   - furniture packages keep the legacy premium model: options × a
//     per-option rate that drops as the option count grows.
//
// One configurator per VR section: "residence" (the floor-plan VR in Condo /
// SFH, the home-model VR in Community) and "amenity". A section's
// configurator only bills while that section has at least one VR tour —
// gated in the calc, not only in the UI, so a stale card can't bill.
//
// Stored on `inputs.vrConfigurator` as { residence, amenity }, each
// { enabled, categories: [{ id, type, customName, optionCount }] }. Quotes
// saved before this have no key and open with both sections off.
//
// The cost is its own printed line in each section, never folded into the VR
// tour price, and is ADDED to vizGross, so the bundle %, revisions and the
// payment plan all see it. Rates are admin-editable (Pricing Admin §
// Real-Time Configurator) via hydrate in ./vexosClientPricing.

export const VR_CONFIGURATOR_LABEL = "Real-Time Configurator";

export const VR_CONFIGURATOR_SECTIONS = ["residence", "amenity"];

export const VR_CONFIGURATOR_CATEGORY_TYPES = [
  { value: "flooring", label: "Flooring" },
  { value: "cabinets", label: "Cabinets" },
  { value: "countertops", label: "Countertops" },
  { value: "backsplash", label: "Backsplash" },
  { value: "appliance_package", label: "Appliance package" },
  { value: "plumbing_fixtures", label: "Plumbing fixtures" },
  { value: "furniture_package", label: "Furniture package" },
  { value: "paint_wall_finish", label: "Paint / wall finish" },
  { value: "lighting_package", label: "Lighting package" },
  { value: "custom", label: "Custom category" },
];

export const FURNITURE_PACKAGE = "furniture_package";

// Legacy charged $2,500 activation and $800/$700/$500 per option for ALL of
// a category's options; Bernard cut the fee to $1,000 and made the first
// option ride on it (2026-10-26). Mutated in place by hydrate when Pricing
// Admin holds saved values.
export const VR_CONFIGURATOR = {
  activationFee: 1000,
  // Standard categories, priced per option IN ORDER: the 1st option, the
  // 2nd, then each further one (the last price repeats).
  standardRates: [0, 700, 500],
  // Furniture packages: one per-option rate for ALL options, by count 1, 2, 3, 4+.
  furnitureRates: [1800, 1500, 1200, 1000],
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const count = (v) => Math.max(1, Math.round(num(v)) || 1);

export function categoryLabel(cat) {
  if (cat?.type === "custom") return (cat.customName || "").trim() || "Custom category";
  return VR_CONFIGURATOR_CATEGORY_TYPES.find((t) => t.value === cat?.type)?.label || "Category";
}

const rung = (ladder, i) => num(ladder[Math.min(i, ladder.length) - 1]);

/** A category's price with `optionCount` options. Standard: each option
 *  priced in order (1st, 2nd, then every further one). Furniture: every
 *  option at the one rate its count earns. */
export function categoryCost(type, optionCount, pricing = VR_CONFIGURATOR) {
  const n = count(optionCount);
  if (type === FURNITURE_PACKAGE) return n * rung(pricing.furnitureRates, n);
  let total = 0;
  for (let k = 1; k <= n; k += 1) total += rung(pricing.standardRates, k);
  return total;
}

const usd = (v) => `$${Math.round(num(v)).toLocaleString("en-US")}`;

/** How a category's price is made up, for the rep's card. */
export function categoryCostDetail(type, optionCount, pricing = VR_CONFIGURATOR) {
  const n = count(optionCount);
  if (type === FURNITURE_PACKAGE) return `${n} × ${usd(rung(pricing.furnitureRates, n))} / option · premium`;
  const parts = [];
  for (let k = 1; k <= Math.min(n, 2); k += 1) {
    const r = rung(pricing.standardRates, k);
    parts.push(`${k === 1 ? "1st" : "2nd"} ${r > 0 ? `+${usd(r)}` : "included"}`);
  }
  if (n > 2) parts.push(`${n - 2} more × +${usd(rung(pricing.standardRates, 3))}`);
  return parts.join(" · ");
}

export function newCategory(type = "flooring") {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, type, customName: "", optionCount: 3 };
}

function normalizeSection(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};
  const categories = Array.isArray(obj.categories)
    ? obj.categories
        .filter((c) => c && typeof c === "object")
        .map((c) => ({
          id: String(c.id || newCategory().id),
          type: VR_CONFIGURATOR_CATEGORY_TYPES.some((t) => t.value === c.type) ? c.type : "flooring",
          customName: typeof c.customName === "string" ? c.customName : "",
          optionCount: count(c.optionCount),
        }))
    : [];
  return { enabled: !!obj.enabled, categories };
}

/** The persisted value: { residence, amenity }. */
export function normalizeVrConfigurator(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};
  return { residence: normalizeSection(obj.residence), amenity: normalizeSection(obj.amenity) };
}

/**
 * One section's price. `active` is false when the section has no VR tour,
 * in which case nothing bills. An enabled section with no categories still
 * bills the activation fee — that's the legacy rule.
 */
export function configuratorCost(section, { active = true, pricing = VR_CONFIGURATOR } = {}) {
  const s = normalizeSection(section);
  if (!active || !s.enabled) return { total: 0, activationFee: 0, categoriesCost: 0, lines: [] };
  const lines = s.categories.map((c) => ({
    name: categoryLabel(c),
    optionCount: c.optionCount,
    total: categoryCost(c.type, c.optionCount, pricing),
    premium: c.type === FURNITURE_PACKAGE,
  }));
  const activationFee = num(pricing.activationFee);
  const categoriesCost = lines.reduce((sum, l) => sum + l.total, 0);
  return { total: activationFee + categoriesCost, activationFee, categoriesCost, lines };
}

/** Both sections, stamped onto the calc fields the surfaces read. */
export function vrConfiguratorFields(cfg, { residenceActive, amenityActive, pricing = VR_CONFIGURATOR } = {}) {
  const v = normalizeVrConfigurator(cfg);
  const residence = configuratorCost(v.residence, { active: residenceActive, pricing });
  const amenity = configuratorCost(v.amenity, { active: amenityActive, pricing });
  return {
    residenceConfiguratorCost: residence.total,
    residenceConfigurator: residence,
    amenityConfiguratorCost: amenity.total,
    amenityConfigurator: amenity,
    vrConfiguratorCost: residence.total + amenity.total,
  };
}

/** The client-facing sub-line: what the buyer can switch between. */
export function configuratorSub(section) {
  const lines = (section && section.lines) || [];
  if (!lines.length) return "Real-time option switching inside the VR tour";
  return lines.map((l) => `${l.name} · ${l.optionCount} option${l.optionCount === 1 ? "" : "s"}`).join(" · ");
}
